/*!
 * Copyright (c) Microsoft Corporation. All rights reserved.
 */
import { IFilterField } from "../Interfaces/IFilterField";
import { IKeyText } from "../Interfaces/IKeyText";
import { ADMIN_LIST } from "./AdminConstants";
import { fieldTypes } from "./FilterFields";
import { milestones } from "./ProfileConstants";

export const defaultListingPageSize = 16;

export const eventStatusTypes = {
  APPROVED: "Approved",
  APPROVEDINCENTIVES: "Approved with Incentives",
  ONHOLD: "On Hold",
  DENIED: "Denied",
  SCHEDULED: "Scheduled",
  SUBMITTED: "Submitted",
};

/**
 * Values for event status
 */
export const eventStatus: IKeyText[] = [
  {
    key: "Scheduled",
    text: eventStatusTypes.SCHEDULED,
  },
  {
    key: "Approved",
    text: eventStatusTypes.APPROVED,
  },
  {
    key: "ApprovedWithIncentives",
    text: eventStatusTypes.APPROVEDINCENTIVES,
  },
  { key: "Returned", text: eventStatusTypes.ONHOLD },
  { key: "Denied", text: eventStatusTypes.DENIED },
  { key: "Submitted", text: eventStatusTypes.SUBMITTED },
];

/**
 * Values for event approval type
 */
export const approvalTypes: IKeyText[] = [
  {
    key: "Approved",
    text: "Approved",
  },
  {
    key: "ApprovedWithIncentives",
    text: "Approved with Incentives",
  },
  {
    key: "Returned",
    text: "On Hold",
  },
  {
    key: "Denied",
    text: "Denied",
  },
];

/**
 * Values for event type
 */
export const eventTypes: IKeyText[] = [
  {
    key: "Conference",
    text: "Conference",
  },
  {
    key: "Hackathon",
    text: "Hackathon",
  },
  {
    key: "Hands-on-Lab",
    text: "Hands-on-Lab",
  },
  {
    key: "Lecture",
    text: "Lecture",
  },
  {
    key: "Meetup",
    text: "Meetup",
  },
  {
    key: "Roundtable/Panel",
    text: "Roundtable/Panel",
  },
  {
    key: "Unconference",
    text: "Unconference",
  },
  {
    key: "User Group",
    text: "User Group",
  },
  {
    key: "Workshop",
    text: "Workshop",
  },
  {
    key: "Other",
    text: "Other",
  },
];

/**
 * Values for event format
 */
export const EVENT_FORMAT_ONLINE_TEXT = "Online";
export const eventFormats: IKeyText[] = [
  {
    key: "Hybrid",
    text: "Hybrid",
  },
  {
    key: "Online",
    text: EVENT_FORMAT_ONLINE_TEXT,
  },
  {
    key: "In-Person",
    text: "In-Person",
  },
];

export const scheduledApprovalStatus = eventStatus.filter((item) => item.key === "Scheduled")[0]
  .key;
export const returnedApprovalStatus = eventStatus.filter((item) => item.key === "Returned")[0].key;

export const EVENT_ORGANIZER = "Organizer";

/**
 * Setting of constants for default field strings
 * we use to set in reducer.
 */
export const EVENT_STATUS = "eventStatus";

export const EVENT_COUNTRY_REGION = "country";

export const EVENT_STATE_PROVINCE = "eventStateProvince";

export const EVENT_TYPE = "eventType";

export const EVENT_FORMAT = "eventFormat";

export const EVENT_START_DATE = "eventStartDate";

export const EVENT_END_DATE = "eventEndDate";

export const EVENT_STUDENT_MILESTONE = "eventStudentMilestone";

export const EVENT_LAST_REVIEWED_BY = "eventLastReviewedBy";

/**
 * Sidebar filter constants used for rendering
 */
export const eventFilterFields: IFilterField[] = [
  {
    key: EVENT_STATUS,
    text: "Event Status",
    type: fieldTypes.MULTI_SELECT,
    options: eventStatus,
  },
  {
    key: EVENT_TYPE,
    text: "Event Type",
    type: fieldTypes.MULTI_SELECT,
    options: eventTypes,
  },
  {
    key: EVENT_FORMAT,
    text: "Event Format",
    type: fieldTypes.FORMAT,
    options: eventFormats,
  },
  {
    key: EVENT_COUNTRY_REGION,
    text: "Country/Region",
    type: fieldTypes.COUNTRY,
  },
  {
    key: EVENT_STATE_PROVINCE,
    text: "State/Province",
    type: fieldTypes.PROVINCE,
  },
  {
    key: EVENT_START_DATE,
    text: "Event Start Date",
    type: fieldTypes.DATE_PICKER,
  },
  {
    key: EVENT_END_DATE,
    text: "Event End Date",
    type: fieldTypes.DATE_PICKER,
  },
  {
    key: EVENT_LAST_REVIEWED_BY,
    text: "Event Last Reviewed By",
    type: fieldTypes.MULTI_SELECT,
    options: ADMIN_LIST,
  },
  {
    key: EVENT_STUDENT_MILESTONE,
    text: "Student Milestone",
    type: fieldTypes.MULTI_SELECT,
    options: milestones,
  },
];

/**
 * Default filter values for events.
 */
export const defaultEventFilterValues: {
  fieldName: string;
  value: string;
}[] = [
  { fieldName: EVENT_STATUS, value: scheduledApprovalStatus },
  { fieldName: EVENT_STATUS, value: returnedApprovalStatus },
];
